import { Person } from '@mui/icons-material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  SolaForceDataGrid,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import {
  AnalyticsReportTypes,
  OverrideManagerReportData,
  useOverrideManagerReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportViewLayout,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';

export const OverrideManagerReport = () => {
  const { t } = useTranslation();
  const { getEmployeeById } = useAllEmployees();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const apiRef = useGridApiRef();

  const { data, isLoading } = useOverrideManagerReportData({
    reportType: {
      type: AnalyticsReportTypes.Company,
    },
  });

  const columns = useMemo((): GridColDef<OverrideManagerReportData>[] => {
    return [
      {
        field: 'fEmpId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 30,
      },
      {
        field: 'fEmpFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'fEmpLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'fEmpUnitId',
        headerName: t('fEmpPersonNumber'),
        valueGetter: ({ row: { fEmpId } }) =>
          getEmployeeById(fEmpId)?.fEmpPersonNumber,
        minWidth: 130,
      },
      {
        field: 'fEmpUnit',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'fEmpUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 110,
      },
      {
        field: 'fEmpOverrideMgrName',
        headerName: t('laOverrideMgr'),
        minWidth: 180,
      },
      {
        field: 'fEmpOverrideMgrNumber',
        headerName: t('laManagerNumber'),
        minWidth: 130,
      },
    ];
  }, [t, goToEmployeeBasicDataView, getEmployeeById]);

  return (
    <ReportViewLayout
      heading={t('laOverrideMgrReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <ReportDatasetRowCount
                count={data.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility apiRef={apiRef} isLoading={isLoading} />
              <ReportDataExport
                title={t('laOverrideMgrReport')}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={data}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fEmpId}
        pinnedColumns={{
          left: ['fEmpId', 'fEmpFirstName', 'fEmpLastName'],
        }}
      />
    </ReportViewLayout>
  );
};
